import React, { useRef, useState } from "react";
import { useSiteContent } from "@/contexts/SiteContentContext";
import { HOME } from "@/lib/homeContent";
import { ScheduleCallButton } from "@/components/ScheduleCallButton";
import { usePersonalization } from "@/lib/personalization";
import { trackEvent } from "@/lib/analytics";
import { useHowWeHelpContent } from "./HowWeHelp";
import { GuideCta } from "@/components/GuideCta";

const K = { fontFamily: "Kanit, Arial, sans-serif" };
const S = { fontFamily: "'Noto Serif', Georgia, serif" };
const STEP_IDS = ["conversation", "path", "connect", "real"];

// Four interactive rows. Hover / focus / tap reveals a route-specific photograph behind the section; text stays in the foreground.
export default function HowItWorks() {
  const { section } = useSiteContent();
  const c = section("how_it_works", HOME.how_it_works);
  const b = section("how_it_works_blocks", HOME.how_it_works_blocks);
  const { profile } = usePersonalization();
  const { steps: routeSteps, route } = useHowWeHelpContent();
  const ctas = section("conversion_ctas", HOME.conversion_ctas);
  const blocks = (Array.isArray(b.blocks) && b.blocks.length ? b.blocks : HOME.how_it_works_blocks.blocks).slice(0, 4);
  const [active, setActive] = useState(null);
  const lastTracked = useRef(null);

  const activate = (i, type) => {
    if (active !== i) setActive(i);
    if (lastTracked.current !== `${i}-${type}`) {
      lastTracked.current = `${i}-${type}`;
      trackEvent("how_it_works_step_interaction", { profile: route, step_id: STEP_IDS[i], interaction_type: type, placement: "home" });
    }
  };

  return (
    <section id="how-it-works" data-testid="how-it-works-section" data-active-step={active === null ? "none" : STEP_IDS[active]} className="relative isolate overflow-hidden bg-[#183129] text-white py-20 md:py-24 px-6 md:px-10 lg:px-14">
      <div aria-hidden="true" className="absolute inset-0 -z-10">
        {routeSteps.map((s, i) => (
          <img
            key={`${route}-${i}`}
            src={s.image}
            alt=""
            loading="lazy"
            data-testid={`hiw-bg-${i + 1}`}
            className={`absolute inset-0 w-full h-full object-cover transition-[opacity,transform] duration-700 ease-out motion-reduce:transition-none motion-reduce:duration-0 motion-reduce:scale-100 ${active === i ? "opacity-100 scale-100" : "opacity-0 scale-[1.03]"}`}
          />
        ))}
        <div className={`absolute inset-0 transition-opacity duration-700 motion-reduce:transition-none motion-reduce:duration-0 ${active === null ? "opacity-0" : "opacity-100"}`} style={{ background: "linear-gradient(90deg, rgba(24,49,41,0.96) 0%, rgba(24,49,41,0.88) 55%, rgba(24,49,41,0.72) 100%)" }} />
      </div>

      <div className="max-w-7xl mx-auto">
        <div className="max-w-3xl">
          <p className="text-[#B3A17D] text-[11px] tracking-[0.3em] uppercase mb-6 font-semibold" style={K}>{c.eyebrow}</p>
          <h2 data-testid="how-it-works-headline" className="text-4xl md:text-5xl leading-[1.05] tracking-tight" style={{ ...K, fontWeight: 600 }}>{c.headline}</h2>
          <p className="mt-6 text-white/80 text-lg md:text-xl leading-relaxed" style={S}>{b.intro}</p>
        </div>

        <ol data-testid="how-it-works-blocks" className="mt-12 md:mt-16 border-t border-white/15" onMouseLeave={() => setActive(null)}>
          {blocks.map((blk, i) => {
            const n = i + 1;
            const on = active === i;
            const dim = active !== null && !on;
            const focus = profile ? b[`${profile}_focus_${n}`] : null;
            const bullets = typeof blk.bullets === "string" ? blk.bullets.split("|").map((x) => x.trim()).filter(Boolean) : [];
            return (
              <li
                key={i}
                data-testid={`hiw-block-${n}`}
                data-active={on}
                tabIndex={0}
                aria-current={on ? "step" : undefined}
                onPointerEnter={(e) => e.pointerType === "mouse" && activate(i, "hover")}
                onPointerDown={(e) => e.pointerType !== "mouse" && activate(i, "tap")}
                onFocus={() => activate(i, "focus")}
                className={`relative grid grid-cols-[40px_1fr] md:grid-cols-[64px_1fr] lg:grid-cols-[64px_4fr_7fr] gap-x-4 md:gap-x-8 gap-y-3 py-8 md:py-10 border-b border-white/15 cursor-default outline-none transition-[opacity,background-color] duration-500 motion-reduce:transition-none focus-visible:ring-2 focus-visible:ring-[#B3A17D] focus-visible:ring-inset ${dim ? "opacity-60" : "opacity-100"} ${on ? "bg-white/[0.04]" : ""}`}
              >
                <span aria-hidden="true" className={`absolute left-0 top-0 h-full w-[2px] bg-[#B3A17D] origin-top transition-transform duration-500 motion-reduce:transition-none ${on ? "scale-y-100" : "scale-y-0"}`} />
                <span className={`text-2xl md:text-3xl leading-none pl-3 md:pl-5 transition-colors duration-500 ${on ? "text-[#B3A17D]" : "text-[#B3A17D]/70"}`} style={K}>0{n}</span>
                <h3 className="text-2xl md:text-[28px] leading-tight" style={{ ...K, fontWeight: 500 }}>{blk.title}</h3>
                <div className="col-start-2 lg:col-start-3 lg:row-start-1">
                  <p className="text-white/80 text-[15px] md:text-base leading-relaxed" style={S}>{blk.text}</p>
                  {focus && (
                    <p data-testid={`hiw-focus-${n}`} className="mt-4 border-l-2 border-[#B3A17D] pl-4 text-[#F5EFE7] text-[15px] md:text-base leading-relaxed" style={S}>{focus}</p>
                  )}
                  {bullets.length > 0 && (
                    <ul className="mt-4 space-y-1.5">
                      {bullets.map((x, j) => (
                        <li key={j} className="flex items-start gap-3 text-white/70 text-[14px] md:text-[15px]"><span className="mt-[9px] w-1.5 h-1.5 shrink-0 bg-[#B3A17D]" />{x}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </li>
            );
          })}
        </ol>

        <div data-testid="hiw-not" className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
          <div className="lg:col-span-8">
            <p className="text-[#B3A17D] text-[10px] tracking-[0.26em] uppercase font-semibold" style={K}>{b.not_headline} <span className="normal-case tracking-normal text-white/70 ml-2" style={S}>{b.not_text}</span></p>
            <ul className="mt-3 flex flex-wrap gap-x-6 gap-y-1.5">
              {String(b.not_items || "").split("|").filter(Boolean).map((x, i) => (
                <li key={i} className="flex items-start gap-2 text-white/75 text-[13px] md:text-[14px]"><span className="mt-[8px] w-1 h-1 shrink-0 bg-[#B3A17D]" />{x.trim()}</li>
              ))}
            </ul>
          </div>
        </div>

        <div data-testid="hiw-cta" className="mt-14 md:mt-16 pt-10 border-t border-white/15 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <p className="text-2xl md:text-3xl leading-tight" style={{ ...K, fontWeight: 500 }}>{ctas.how_it_works_cta_heading}</p>
            <div className="mt-5"><ScheduleCallButton source="how_it_works" tone="sand" label={c.cta} /></div>
            <p className="mt-3 text-white/60 text-[12px] tracking-[0.12em] uppercase" style={K}>{ctas.call_meta}</p>
          </div>
          <GuideCta placement="how_it_works" tone="dark" className="lg:col-span-4 lg:col-start-9" />
        </div>
      </div>
    </section>
  );
}
